/**
 * What a cut-off workflow run still owes, as the agent reports it
 * (`GET /workflows/:code/threads/:threadId/continue`) — the pure rules the
 * Continue control works from. Import-free so `tests/continueRun.test.mjs` can
 * transpile this file alone.
 */

import type { PendingWork } from "../../../../shared/workflow-continue";

export type { PendingWork };

export const NO_PENDING_WORK: PendingWork = { kind: "none" };

/** The agent's answer as `PendingWork`; anything unreadable is "nothing to continue", never a button for a guess. */
export function parsePendingWork(body: unknown): PendingWork {
  const pending = (body as { pending?: unknown } | null)?.pending as Record<string, unknown> | undefined;
  if (!pending || typeof pending !== "object") return NO_PENDING_WORK;
  switch (pending["kind"]) {
    case "model":
      return { kind: "model" };
    case "interrupt":
      return { kind: "interrupt" };
    case "tools": {
      const calls = Array.isArray(pending["calls"])
        ? pending["calls"].flatMap((call) =>
            call && typeof call === "object" && typeof (call as { name?: unknown }).name === "string"
              ? [{ id: typeof (call as { id?: unknown }).id === "string" ? (call as { id: string }).id : "", name: (call as { name: string }).name }]
              : [])
        : [];
      const destructive = Array.isArray(pending["destructive"]) ? pending["destructive"].filter((name): name is string => typeof name === "string") : [];
      return { kind: "tools", calls, destructive };
    }
    default:
      return NO_PENDING_WORK;
  }
}

/** A run the user can pick up: cut in the model step or in a tool step. An approval is answered, not continued. */
export function continuable(pending: PendingWork): boolean {
  return pending.kind === "model" || pending.kind === "tools";
}

/** The tools that may already have changed something, so the user is asked before they run again. */
export function destructiveInFlight(pending: PendingWork): string[] {
  return pending.kind === "tools" ? pending.destructive : [];
}
